import React, { useCallback, useReducer, useEffect } from 'react'
import { useHistory } from 'react-router-dom'
import { useDispatch, useSelector, shallowEqual } from 'react-redux'

import {
  useTheme,
  useMediaQuery,
  makeStyles,
  Grid,
  Divider,
  List,
  ListItem,
  ListItemText,
  Button,
  Typography,
} from '@material-ui/core'

import { useLiterals } from '~/components/Literals'

import ProductItem from './ProductItem'
import Placeholder from './Placeholder'

import actions from '~/actions'
import track from '~/track'
import { selectProducts, viewSelector } from '~/selectors'

function getSlug(value) {
  return value
    .toLowerCase()
    .replace(/\s+/g, '-')
    .replace(/[^a-z0-9-]/g, '')
}

const useStyles = makeStyles((theme) => ({
  text: {
    flex: '0 1 auto',
    '& span': {
      ...theme.lineLimit(2),
      maxHeight: 52,
      overflow: 'hidden',
    },
    '& p': {
      margin: 0,
      fontSize: 13,
      ...theme.lineLimit(2),
      maxHeight: 42,
    },
  },
  subtitle: {
    fontSize: 10,
    color: '#707070',
    background: '#EEE',
    textTransform: 'uppercase',
    padding: '8px 8px 8px 16px',
  },
  root: {
    paddingTop: 0,
    paddingBottom: 0,
  },
  itemAnyProduct: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    background: '#FFF',
    padding: '8px 16px',
    [theme.breakpoints.down('sm')]: {
      gap: 8,
      flexDirection: 'row',
      justifyContent: 'center',
    },
    [theme.breakpoints.down('xs')]: {
      flexDirection: 'column',
    },
  },
  textAnyProduct: {
    '& > span': {
      display: 'none',
    },
    [theme.breakpoints.down('sm')]: {
      order: 2,
      textTransform: 'lowercase',

      '& > span': {
        display: 'inline',
      },
    },
  },
  orAnyProduct: {
    display: 'inline',
    [theme.breakpoints.down('sm')]: {
      display: 'none',
    },
  },
  buttonAnyProduct: {
    textTransform: 'none',
    backgroundColor: 'rgba(238, 235, 235, .9)',
    [theme.breakpoints.down('sm')]: {
      order: 1,
    },
  },
}))

function AnyProduct({ classes, onClick, getLiteral }) {
  const config = useSelector((state) => state.config)

  return (
    <>
      <ListItem className={classes.itemAnyProduct}>
        <Typography variant="body1" className={classes.textAnyProduct}>
          <span>{getLiteral('2818', ['', '']).trim()} </span>
          {getLiteral('1357')}
          <span>:</span>
        </Typography>
        <Typography variant="body1" component="span" className={classes.orAnyProduct}>
          {getLiteral('2818', ['', '']).trim()}
        </Typography>
        <ListItemText className={classes.text}>
          <Button variant="contained" className={classes.buttonAnyProduct} disableElevation onClick={onClick}>
            {config.listingPlaceholderStore || `${getLiteral('1054')} ${getLiteral('1996')}`}
          </Button>
        </ListItemText>
      </ListItem>
      <Divider />
    </>
  )
}

function ProductList({ position }) {
  const history = useHistory()
  const classes = useStyles()
  const theme = useTheme()
  const dispatch = useDispatch()
  const view = useSelector(viewSelector)
  const {
    loading,
    products: originalProducts,
    searchProductResults,
    fromSearch,
    config,
  } = useSelector(selectProducts, shallowEqual)
  const { getLiteral } = useLiterals()
  const mobile = useMediaQuery(theme.breakpoints.down('xs'))

  const productsIsSearched = searchProductResults && searchProductResults.length > 0
  const products = searchProductResults ? searchProductResults : originalProducts

  const isMobileVersionLess360 = view === 'VIEW_SEARCH_PRODUCTS' && mobile

  const handleSelectProduct = useCallback(
    (product) => {
      if (!position.user?.lat && !position.user?.lng) {
        position.handlerShowPositionDialog(true)
        return
      }

      if (config.multiProduct) dispatch(actions.selectProduct(product))

      const select = () => {
        history.push(`/produto/${getSlug(product.name)}/${product.gtin}`, {
          from: `${history.location.pathname}${history.location.search}`,
        })
        track('selecionarProduto', { category: 'menu', name: product.name })
      }

      select()
    },
    [config, position],
  )

  const handleSelectAll = useCallback(() => {
    const searchAll = () => {
      track('buscarQualquerProduto', { name: 'todos', category: 'produto' })
      history.push('/produto/todos')
    }

    if (position.user?.lat && position.user?.lng) {
      searchAll()
    } else {
      position.handlerShowPositionDialog(true)
    }
  }, [position])

  return (
    <Grid className={classes.wrapper}>
      {loading && <Placeholder />}

      {!loading && products && !products.length && fromSearch && (
        <Grid style={{ padding: 16 }}>{getLiteral('1884')}</Grid>
      )}

      {!loading && products && (
        <>
          {!isMobileVersionLess360 && config.anyProducts === 'header' && (
            <AnyProduct classes={classes} onClick={handleSelectAll} getLiteral={getLiteral} />
          )}

          {view === 'VIEW_SEARCH_PRODUCTS' && (
            <Grid style={{ padding: 16, borderBottom: '1px solid #EEEBEB' }}>
              {getLiteral('2817', [products.length])}
            </Grid>
          )}

          <List id="product-list" classes={{ root: classes.root }}>
            {!productsIsSearched &&
              products
                .filter((product) => config?.featured?.some((feat) => feat.gtin === product.gtin))
                .map((product) => (
                  <ProductItem
                    key={product.gtin}
                    product={product}
                    onSelectProduct={handleSelectProduct}
                    position={position}
                  />
                ))}

            {products
              .filter((product) =>
                productsIsSearched ? [] : !config?.featured?.some((feat) => product.gtin === feat.gtin),
              )
              .map((product, index) => (
                <ProductItem
                  key={product.gtin}
                  product={product}
                  onSelectProduct={handleSelectProduct}
                  position={position}
                />
              ))}
          </List>

          {!isMobileVersionLess360 && (!config.anyProducts || config.anyProducts === 'footer') && (
            <AnyProduct classes={classes} onClick={handleSelectAll} getLiteral={getLiteral} />
          )}
        </>
      )}
    </Grid>
  )
}

export default ProductList
